import { useState, useEffect, useRef } from "react";
import { MdCrop } from "react-icons/md";
import { IoPlayCircleOutline, IoText } from "react-icons/io5";
import { FaMicrophone, FaRegPlayCircle } from "react-icons/fa";
import { RiImageEditFill } from "react-icons/ri";
import { FaRegCirclePause } from "react-icons/fa6";

const EditingTools = ({ recordedVideoUrl, elapsedTime, onEditClick, onTextClick, videoRef }) => {
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playVideo, setPlayVideo] = useState(false);
    const mainVideoRef = useRef(null);

    // Format time as MM:SS
    const formatTime = (timeInSeconds) => {
        if (isNaN(timeInSeconds) || timeInSeconds < 0 || timeInSeconds === Infinity) {
            return "00:00";
        }

        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Handle video time updates
    useEffect(() => {
        const video = mainVideoRef.current;
        if (!video || !recordedVideoUrl) return;

        // Always use elapsedTime as the authoritative duration
        // This accounts for pauses and total recording time
        if (elapsedTime > 0) {
            setDuration(elapsedTime);
        }

        const handleTimeUpdate = () => {
            setCurrentTime(video.currentTime);
        };

        // Keep the UI in sync with play/pause state
        const handlePlay = () => setPlayVideo(true);
        const handlePause = () => setPlayVideo(false);

        const handleLoadedMetadata = () => {
            // Always prioritize elapsedTime over video.duration
            // because video.duration only reflects actual recorded content
            // not the total time including pauses
            if (elapsedTime > 0) {
                setDuration(elapsedTime);
            } else if (video.duration && !isNaN(video.duration) && video.duration > 0) {
                setDuration(video.duration);
            }
        };

        const handleCanPlay = () => {
            video.play().catch(error => {
            });
        };

        const handleError = (e) => {
            console.error('Video error:', e);
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('error', handleError);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);

        // Force load metadata if video is ready
        if (video.readyState >= 1) {
            handleLoadedMetadata();
        }

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('error', handleError);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
        };
    }, [recordedVideoUrl, elapsedTime]);

    // Safe function to get valid segment count
    const getSegmentCount = () => {
        // ALWAYS use elapsedTime as the authoritative source for segment count
        // because it represents the total recording time including pauses
        // whereas video.duration only reflects actual recorded content
        const totalTime = elapsedTime || 1;

        if (!totalTime || totalTime <= 0) {
            return 1; // Show at least 1 segment
        }

        const segmentCount = Math.ceil(totalTime);

        // Ensure we have a valid positive number for array length
        if (isNaN(segmentCount) || segmentCount <= 0 || segmentCount === Infinity) {
            return 1; // Fallback to 1 segment
        }

        // Limit to reasonable maximum to prevent performance issues
        return Math.min(segmentCount, 300); // Max 5 minutes at 1 segment per second
    };

    // Get the display duration - always use elapsedTime
    const getDisplayDuration = () => {
        return elapsedTime > 0 ? elapsedTime : duration;
    };

    // toggle play/pause for the main video and preview video
    const togglePlay = () => {
        const video = mainVideoRef.current;
        const videoPreview = videoRef.current;

        if (!video) return;

        if (video.paused || video.ended) {
            // Play both main and preview video
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise.catch((error) => {
                });
            }
            if (videoPreview && videoPreview.paused) {
                videoPreview.play().catch((error) => {
                });
            }
        } else {
            // Pause both main and preview video
            try {
                video.pause();
                if (videoPreview) {
                    videoPreview.pause();
                }
            } catch (error) {
              console.error(error);
            }
        }
    };
    return (
        <div className="absolute bottom-0 w-full flex flex-col items-center justify-center mb-3">
            {/* Fixed Time Display - Outside scrollable container */}




            {recordedVideoUrl && (
                <div className="mb-1 mx-2 pl-14 w-full relative">

                    <div className="absolute -top-6 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded z-20">
                        {formatTime(currentTime)} / {formatTime(getDisplayDuration())}
                    </div>
                    <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlay(); } }}
                        onClick={() => togglePlay()}
                        className="absolute z-50 top-1 left-2 cursor-pointer select-none"
                        aria-label={playVideo ? 'Pause video' : 'Play video'}
                    >
                        {playVideo ? <FaRegCirclePause className="text-white text-[2rem]" /> : <FaRegPlayCircle className="text-white text-[2rem]" />}
                    </div>
                </div>
            )}

            {/* Recording Preview Scroller */}
            <div className="mb-3 mx-2 pl-14 h-10 w-full overflow-x-auto relative">
                {recordedVideoUrl ? (
                    <div className="relative">
                        {/* Main Video (Hidden, for time tracking) */}
                        <video
                            ref={mainVideoRef}
                            src={recordedVideoUrl}
                            // muted
                            loop
                            className="absolute invisible"
                            disablePictureInPicture
                            preload="metadata"
                        />

                        {/* Preview Segments */}
                        <div className="flex h-full" style={{ width: 'max-content' }}>
                            {Array.from({ length: getSegmentCount() }, (_, index) => (
                                <div key={index} className="h-full w-16 bg-gray-400 overflow-hidden flex-shrink-0 relative border border-gray-500">
                                    <video
                                        muted
                                        src={recordedVideoUrl}
                                        disablePictureInPicture
                                        className="object-cover w-full h-full"
                                        preload="metadata"
                                        onLoadedData={(e) => {
                                            e.target.currentTime = index;
                                        }}
                                        onError={(e) => {
                                        }}
                                    />
                                    {/* Highlight current segment */}
                                    {Math.floor(currentTime) === index && (
                                        <div className="absolute top-0 left-0 w-full h-full border-2 border-[#38B6FF] pointer-events-none"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center rounded">
                        <p className="text-white text-xs">No recording available</p>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-x-3">
                <div className="flex flex-col items-center">
                    <button className="flex items-center justify-center h-8 w-12 rounded bg-[#BDE6FF] text-black">
                        <MdCrop className="text-[20px]" />
                    </button>
                    <p className="text-[11px] mt-0.5 text-white">Crop</p>
                </div>
                <div className="flex flex-col items-center">
                    <button
                        onClick={onEditClick}
                        className="flex items-center justify-center h-8 w-12 rounded bg-[#BDE6FF] text-black"
                    >
                        <RiImageEditFill className="text-[20px]" />
                    </button>
                    <p className="text-[11px] mt-0.5 text-white">Edit</p>
                </div>
                <div className="flex flex-col items-center">
                    <button
                        onClick={onTextClick}
                        className="flex items-center justify-center h-8 w-12 rounded bg-[#BDE6FF] text-black"
                    >
                        <IoText className="text-[20px]" />
                    </button>
                    <p className="text-[11px] mt-0.5 text-white">Text</p>
                </div>
                <div className="flex flex-col items-center">
                    <button className="flex items-center justify-center h-8 w-12 rounded bg-[#BDE6FF] text-black">
                        <FaMicrophone className="text-[20px]" />
                    </button>
                    <p className="text-[11px] mt-0.5 text-white">Voice Over</p>
                </div>
            </div>
        </div>
    );
};

export default EditingTools;